import { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Build as ToolIcon,
  CheckCircle as DoneIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import type { ChatMessage } from './types';

/** Human-readable tool names */
const TOOL_LABELS: Record<string, string> = {
  searchStudies: 'Search Studies',
  getStudyDetails: 'Study Details',
  getStudyReport: 'Study Report',
  getStudyHistory: 'Study History',
  getSystemSummary: 'System Summary',
  getQueueStatus: 'Queue Status',
  getMessages: 'Messages',
  getPatient: 'Patient Info',
  getSites: 'Sites',
  sendMessage: 'Send Message',
  acknowledgeMessage: 'Acknowledge Message',
  getSessionLogs: 'Session Logs',
  getUsersPaged: 'Users',
  holdStudy: 'Hold Study',
  unholdStudy: 'Unhold Study',
  markUrgent: 'Mark Urgent',
  assignStudy: 'Assign Study',
  updateStudyStatus: 'Update Status',
  retryFailedQueue: 'Retry Queue',
};

interface ToolCallCardProps {
  message: ChatMessage;
}

export const ToolCallCard = ({ message }: ToolCallCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const meta = message.metadata;
  const toolName = meta?.tool ?? 'unknown';
  const label = TOOL_LABELS[toolName] ?? toolName;
  const isComplete = message.role === 'tool_result' || meta?.result !== undefined;
  const hasError = meta?.result && 'error' in meta.result;

  const argsStr = meta?.args ? summarizeArgs(meta.args) : '';
  const resultData = meta?.browserResult ?? meta?.result ?? null;

  return (
    <Box sx={{ my: 0.5, ml: 2, maxWidth: '90%' }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.5,
            backgroundColor: 'action.hover',
            cursor: resultData ? 'pointer' : 'default',
          }}
          onClick={() => resultData && setExpanded(!expanded)}
        >
          {isComplete ? (
            hasError ? (
              <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
            ) : (
              <DoneIcon sx={{ fontSize: 16, color: 'success.main' }} />
            )
          ) : (
            <CircularProgress size={14} />
          )}

          <ToolIcon sx={{ fontSize: 14, color: 'text.secondary' }} />

          <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, flex: 1 }}>
            {label}
          </Typography>

          {argsStr && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {argsStr}
            </Typography>
          )}

          {resultData && (
            <Tooltip title={expanded ? 'Hide details' : 'Show details'}>
              <IconButton size="small" sx={{ p: 0.25 }}>
                {expanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Summary line */}
        {isComplete && !expanded && (
          <Box sx={{ px: 1, py: 0.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {summarizeResult(meta?.result)}
            </Typography>
          </Box>
        )}

        {/* Expandable details */}
        <Collapse in={expanded}>
          <Box
            sx={{
              px: 1,
              py: 0.75,
              borderTop: '1px solid',
              borderColor: 'divider',
              maxHeight: 300,
              overflowY: 'auto',
              backgroundColor: 'background.default',
            }}
          >
            <Typography
              variant="body2"
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: 11,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                m: 0,
              }}
            >
              {JSON.stringify(resultData, null, 2)}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

/** Summarize tool arguments into a short string */
function summarizeArgs(args: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(args)) {
    if (value === undefined || value === null) continue;
    const str = typeof value === 'string' ? value : JSON.stringify(value);
    parts.push(`${key}: ${str.length > 30 ? str.slice(0, 30) + '…' : str}`);
  }
  const joined = parts.join(', ');
  return joined.length > 60 ? joined.slice(0, 60) + '…' : joined;
}

/** Summarize tool result into a short string */
function summarizeResult(result?: Record<string, unknown>): string {
  if (!result) return 'Complete';
  if ('error' in result) return `Error: ${result.error}`;
  if ('count' in result) return `${result.count} results`;
  if ('studies' in result && Array.isArray(result.studies)) return `${result.studies.length} studies`;
  if ('status' in result) return `Status: ${result.status}`;
  return 'Complete';
}
